/**
 * Variance & Comparison Engine
 * 
 * Compares Actual Monthly Reports against Simulation Months
 */

import { ActualMonthlyReport } from '@/lib/db/schema/actuals'
import { SimulationMonth, SimulationRun } from '@/lib/db/schema/simulations'
import { calculateIncomeStatement } from './financialMetrics'

export interface VarianceResult {
  revenueVariance: number
  grossProfitVariance: number
  netProfitVariance: number
  cashVariance: number
  equityVariance: number
  
  revenueVariancePct: number
  netProfitVariancePct: number
  
  interpretation: string
}

export function calculateVariance(
  actual: ActualMonthlyReport,
  simulationMonth: SimulationMonth,
  simulationRun: SimulationRun
): VarianceResult {
  // 1. Calculate actual P&L first since it's not stored
  const actualPl = calculateIncomeStatement({
    revenue: actual.revenue,
    cogs: actual.cogs,
    operatingExpense: actual.operatingExpense,
    tax: actual.tax,
    otherExpense: actual.otherExpense + actual.interest
  })

  // 2. Variances (Actual - Simulation). Positive is generally "better" (except maybe costs, but we focus on output metrics)
  const revenueVariance = actual.revenue - simulationMonth.revenue
  const grossProfitVariance = actualPl.grossProfit - simulationMonth.grossProfit
  const netProfitVariance = actualPl.netProfit - simulationMonth.netProfit
  const cashVariance = actual.cashBalance - simulationMonth.endingCash
  
  // NOTE: actual doesn't store endingEquity directly. We approximate it by
  // using netProfit variance if we don't have a full actual equity tracker yet.
  // For now, let's just use net profit variance as a proxy for equity growth variance in that month.
  const equityVariance = netProfitVariance + (actual.additionalCapital - simulationMonth.additionalCapital) - (actual.ownerWithdrawal - simulationMonth.ownerWithdrawal)

  const revenueVariancePct = simulationMonth.revenue > 0 ? (revenueVariance / simulationMonth.revenue) * 100 : 0
  const netProfitVariancePct = Math.abs(simulationMonth.netProfit) > 0 ? (netProfitVariance / Math.abs(simulationMonth.netProfit)) * 100 : 0

  // 3. Interpretation
  let interpretation = ''
  
  if (revenueVariance >= 0 && netProfitVariance >= 0) {
    if (simulationRun.type === 'optimistic') {
      interpretation = 'Luar Biasa! Performa aktual berhasil melampaui skenario Optimis.'
    } else if (simulationRun.type === 'moderate') {
      interpretation = 'Sangat Baik. Performa aktual melampaui ekspektasi Moderat.'
    } else if (simulationRun.type === 'conservative') {
      interpretation = 'Cukup Baik. Performa berada di atas skenario Konservatif, namun perhatikan potensi upside yang hilang.'
    } else {
      interpretation = 'Performa aktual berada di atas target simulasi manual.'
    }
  } else if (revenueVariance < 0 && netProfitVariance < 0) {
    if (simulationRun.type === 'optimistic') {
      interpretation = 'Wajar. Performa di bawah skenario Optimis. Coba bandingkan dengan skenario Moderat.'
    } else if (simulationRun.type === 'moderate') {
      interpretation = 'Peringatan. Performa di bawah target Moderat. Perlu efisiensi.'
    } else if (simulationRun.type === 'conservative') {
      interpretation = 'Bahaya! Performa bahkan lebih buruk dari skenario Konservatif terburuk. Cek kembali pengeluaran Anda.'
    } else {
      interpretation = 'Performa aktual berada di bawah target simulasi.'
    }
  } else if (revenueVariance >= 0 && netProfitVariance < 0) {
    interpretation = 'Inefisiensi Terdeteksi. Pendapatan melebihi target, namun Laba Bersih di bawah target. Biaya membengkak!'
  } else {
    interpretation = 'Efisiensi Tinggi. Pendapatan di bawah target, namun Laba Bersih melampaui target karena penghematan biaya.'
  }

  return {
    revenueVariance,
    grossProfitVariance,
    netProfitVariance,
    cashVariance,
    equityVariance,
    revenueVariancePct,
    netProfitVariancePct,
    interpretation
  }
}
